// src/components/SuperAdminDashboard.js
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Icon from './Icon';
import BrandIcon from './BrandIcon';
import { useModal } from '../context/ModalContext';
import { useOrgBrand } from '../context/OrgBrandContext';
import Layout from './Layout';
import ProxyUser from './ProxyUser';
import MeetingsPanel from './MeetingsPanel';
import AgencyHubPanel from './AgencyHubPanel';
import ToDoList from './ToDoList';
import ContentCreation from './ContentCreation';
import CreateIdeas from './CreateIdeas';
import SourcesPanel from './SourcesPanel';
import ImageUpload from './ImageUpload';
import Recordings from './Recordings';
import Gemini from './Gemini';
import IdeasPanel from './IdeasPanel';
import ReviewContentPanel from './ReviewContentPanel';
import SchedulePostsPanel from './SchedulePostsPanel';
import BrandAssets from './BrandAssets';
import BrandVoiceDesigner from './BrandVoiceDesigner';
import Logs from './Logs';
import InvitationsPanel from './InvitationsPanel';
import ManagementPanel from './ManagementPanel';
import ProfilePanel from './ProfilePanel';
import NotificationsPanel from './NotificationsPanel';
import WelcomeModal from './WelcomeModal';
import OnboardingWizard from './OnboardingWizard';
import OnboardingRoleSelector from './OnboardingRoleSelector';
import OrganizationsBrandsPanel from './OrganizationsBrandsPanel';
import DashboardHome from './DashboardHome';
import ActivityLogPanel from './ActivityLogPanel';
import PromptHub from './PromptHub';
import LiveKitMeetings from './LiveKitMeetings';
import LiveKitRecordings from './LiveKitRecordings';
import { LIVEKIT_ENABLED } from '../config/features';
import '../styles/SuperAdminDashboard.css';
import CustomSelect from './CustomSelect';
import '../styles/GuestCompletions.css';

const SuperAdminDashboard = ({ user, onLogout, onUserUpdate }) => {
  const modal = useModal();
  const { clearSelection, refresh } = useOrgBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [showOnboardingRoleSelector, setShowOnboardingRoleSelector] = useState(false);
  const [selectedOnboardingRole, setSelectedOnboardingRole] = useState(null);

  // Read activeSection from URL params, default to 'dashboard'
  const activeSection = searchParams.get('section') || 'dashboard';

  // Update URL when section changes
  const setActiveSection = async (section) => {
    // If navigating away from the proxy panel, always clear proxy session first
    if (activeSection === 'proxy-user' && section !== 'proxy-user') {
      try {
        await axios.post('/api/super-admin/clear-role-assignments', {}, { withCredentials: true });
      } catch (e) {}
    }
    setSearchParams({ section });
  };
  const [organizationForm, setOrganizationForm] = useState({
    organization_name: '',
    owner_id: '',
    icon_url: ''
  });
  const [brandForm, setBrandForm] = useState({
    brand_name: '',
    organization_id: '',
    owner_id: '',
    icon_url: ''
  });
  const [orgBrandsActiveSection, setOrgBrandsActiveSection] = useState('overview');
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [editingOrganization, setEditingOrganization] = useState(null);
  const [editOrgForm, setEditOrgForm] = useState({
    organization_name: '',
    owner_id: '',
    icon_url: ''
  });
  const [editingBrand, setEditingBrand] = useState(null);
  const [editBrandForm, setEditBrandForm] = useState({
    brand_name: '',
    organization_id: '',
    owner_id: '',
    icon_url: ''
  });
  const [activeRole, setActiveRole] = useState('super_admin'); // New state for role switching
  const [inviteForm, setInviteForm] = useState({
    full_name: '',
    email: '',
    user_type: 'client',
    organization_id: '',  // For assigning clients to specific organizations
    brand_id: '',  // For assigning clients to specific brands
    agency_id: '',  // For assigning admins/operators to specific agencies
    agency_name: ''
  });
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [cancellingInvitations, setCancellingInvitations] = useState(new Set());
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState(null);
  const [userMgmtSection, setUserMgmtSection] = useState(() => {
    return localStorage.getItem('superadmin_user_mgmt_tab') || 'invitations';
  });
  const [orgBrandsSection, setOrgBrandsSection] = useState(() => {
    return localStorage.getItem('superadmin_org_brands_tab') || 'create-org';
  });

  useEffect(() => {
    localStorage.setItem('superadmin_user_mgmt_tab', userMgmtSection);
  }, [userMgmtSection]);

  useEffect(() => {
    localStorage.setItem('superadmin_org_brands_tab', orgBrandsSection);
  }, [orgBrandsSection]);
  const [agencyOnboardForm, setAgencyOnboardForm] = useState({
    agency_name: '',
    admin_email: '',
    admin_name: '',
    plan: 'basic'
  });
  const [agencyOnboardLoading, setAgencyOnboardLoading] = useState(false);
  const [activeAgency, setActiveAgency] = useState(null);
  const [orgRefreshTrigger, setOrgRefreshTrigger] = useState(0);
  const [guestCompletions, setGuestCompletions] = useState([]);
  const [guestCompletionsLoading, setGuestCompletionsLoading] = useState(false);
  const [deletingGuestCompletions, setDeletingGuestCompletions] = useState(new Set());
  const [casperStatus, setCasperStatus] = useState(null);
  const [casperLoading, setCasperLoading] = useState(false);
  const [casperLogs, setCasperLogs] = useState([]);
  const [casperLogsLoading, setCasperLogsLoading] = useState(false);
  const [casperActiveTab, setCasperActiveTab] = useState('bot'); // 'bot' or 'logs'
  const [casperStats, setCasperStats] = useState(null);
  const [casperStatsLoading, setCasperStatsLoading] = useState(false);
  const [casperAssignments, setCasperAssignments] = useState([]);
  const [casperAssignmentsLoading, setCasperAssignmentsLoading] = useState(false);
  const [casperProcessing, setCasperProcessing] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [newAgencyForm, setNewAgencyForm] = useState({
    agency_name: ''
  });

  useEffect(() => {
    // Only fetch on mount
    fetchDashboardData();
    fetchPendingInvitations();
  }, []);

  // Refresh dashboard when agency changes
  const handleAgencyChange = async (agency, options = {}) => {
    // Set the active agency first
    setActiveAgency(agency);

    // If this is the initial load, skip clearing and refetching
    // OrgBrandContext is already initializing with the correct data
    if (options.isInitialLoad) {
      return;
    }

    // Clear org/brand selection when switching agencies
    try {
      // Clear frontend state synchronously
      localStorage.removeItem('selected_organization_id');
      localStorage.removeItem('selected_brand_id');

      // Clear context immediately to update UI components
      clearSelection(true);

      // Clear backend session and database preferences
      await axios.post('/api/user/set-organization-context', {
        organization_id: null
      }, { withCredentials: true });

      await axios.post('/api/user/preferences', {
        organization_id: null,
        brand_id: null
      }, { withCredentials: true });

      // Refresh organizations and brands for the new agency context
      await refresh();
    } catch (error) {
      console.error('Error handling agency change:', error);
    }

    refreshDashboardData(); // Use silent refresh to avoid unmounting children
    // Trigger org selector refresh
    setOrgRefreshTrigger(prev => prev + 1);
  };

  const handleOrganizationChange = async (organization) => {
    try {
      // Organization context is already set on backend via OrganizationSelector
      // Use silent refresh to avoid unmounting children and triggering duplicate fetches
      refreshDashboardData();
    } catch (error) {
      console.error('Error switching organization:', error);
    }
  };

  useEffect(() => {
    if (activeSection === 'calendar') {
      fetchCalendarEvents();
    }
  }, [activeSection]);

  useEffect(() => {
    if (activeSection === 'guest-completions') {
      fetchGuestCompletions();
    }
  }, [activeSection]);

  useEffect(() => {
    if (activeSection === 'casper') {
      fetchCasperStatus();
      fetchCasperLogs();
      fetchCasperStats();
      fetchCasperAssignments();
    }
  }, [activeSection, activeAgency]);

  useEffect(() => {
    if (user) {
      if (user.welcome_seen === false) {
        setShowWelcomeModal(true);
      }
      // Super admins can manually trigger onboarding with role selector
      // Don't auto-trigger for super_admins since they need to pick a role
    }
  }, [user]);

  const handleOpenWelcome = () => {
    setShowWelcomeModal(true);
  };

  const handleWelcomeComplete = async () => {
    try {
      await axios.post('/api/user/mark-welcome-seen');
      if (user.onboarding_seen === false) {
        setShowOnboardingRoleSelector(true);
      }
    } catch (error) {
      console.error('Failed to mark welcome as seen:', error);
    }
  };

  const handleOpenOnboarding = () => {
    // Super admins can select which role onboarding to test
    setShowOnboardingRoleSelector(true);
  };

  const handleRoleSelect = (role) => {
    setSelectedOnboardingRole(role);
    setShowOnboardingWizard(true);
  };

  const handleOnboardingComplete = async () => {
    try {
      await axios.post('/api/user/mark-onboarding-seen');
    } catch (error) {
      console.error('Failed to mark onboarding as seen:', error);
    }
  };

  const handleOnboardingSkip = async () => {
    try {
      await axios.post('/api/user/mark-onboarding-seen');
    } catch (error) {
      console.error('Failed to mark onboarding as seen:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/super-admin/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      modal.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Silent refresh - updates data without showing the full loading spinner,
  // which would unmount panels and reset their sub-panel state
  const refreshDashboardData = async () => {
    try {
      const response = await axios.get('/api/super-admin/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error refreshing dashboard data:', error);
    }
  };

  const fetchPendingInvitations = async () => {
    try {
      const response = await axios.get('/api/super-admin/invitations');
      setPendingInvitations(response.data.invitations || []);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  const fetchGuestCompletions = async () => {
    try {
      setGuestCompletionsLoading(true);
      const response = await axios.get('/api/super-admin/guest-completions');
      setGuestCompletions(response.data.completions || []);
    } catch (error) {
      console.error('Error fetching guest completions:', error);
    } finally {
      setGuestCompletionsLoading(false);
    }
  };

  const handleDeleteGuestCompletion = async (guestEmail, guestName) => {
    const result = await modal.confirm(`Are you sure you want to delete the guest completion for ${guestName || guestEmail}? This action cannot be undone.`, 'Delete Guest Completion');
    if (!result) {
      return;
    }

    setDeletingGuestCompletions(prev => new Set([...prev, guestEmail]));

    try {
      const response = await axios.delete(`/api/super-admin/guest-completions/${encodeURIComponent(guestEmail)}`);

      if (response.data.success) {
        modal.success('Guest completion deleted successfully!');
        fetchGuestCompletions(); // Refresh the list
      }
    } catch (error) {
      console.error('Error deleting guest completion:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete guest completion';
      modal.error(errorMessage);
    } finally {
      setDeletingGuestCompletions(prev => {
        const newSet = new Set(prev);
        newSet.delete(guestEmail);
        return newSet;
      });
    }
  };

  const fetchCasperStatus = async () => {
    try {
      setCasperLoading(true);
      const response = await axios.get('/api/super-admin/casper/status');
      setCasperStatus(response.data);
    } catch (error) {
      console.error('Error fetching Casper status:', error);
      modal.error('Failed to fetch Casper status');
    } finally {
      setCasperLoading(false);
    }
  };

  const fetchCasperLogs = async () => {
    try {
      setCasperLogsLoading(true);
      const response = await axios.get('/api/super-admin/casper/logs?limit=100');
      setCasperLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching Casper logs:', error);
      // Don't show error modal if no agency selected (handled by status check)
      if (error.response?.status !== 400) {
        modal.error('Failed to fetch Casper logs');
      }
    } finally {
      setCasperLogsLoading(false);
    }
  };

  const fetchCasperStats = async () => {
    try {
      setCasperStatsLoading(true);
      const response = await axios.get('/api/super-admin/casper/stats');
      setCasperStats(response.data);
    } catch (error) {
      console.error('Error fetching Casper stats:', error);
      if (error.response?.status !== 400) {
        modal.error('Failed to fetch Casper stats');
      }
    } finally {
      setCasperStatsLoading(false);
    }
  };

  const fetchCasperAssignments = async () => {
    try {
      setCasperAssignmentsLoading(true);
      const response = await axios.get('/api/super-admin/casper/assignments');
      setCasperAssignments(response.data.assignments || []);
    } catch (error) {
      console.error('Error fetching Casper assignments:', error);
      if (error.response?.status !== 400) {
        modal.error('Failed to fetch Casper assignments');
      }
    } finally {
      setCasperAssignmentsLoading(false);
    }
  };

  const handleProcessAllPending = async () => {
    try {
      setCasperProcessing(true);
      const response = await axios.post('/api/casper/process', {});

      if (response.data.processed > 0) {
        modal.success(`Successfully processed ${response.data.processed} items! (${response.data.ideas_processed || 0} ideas, ${response.data.drafts_processed || 0} drafts)`);
      } else {
        modal.info('No pending items to process');
      }

      // Refresh stats, logs, and assignments
      fetchCasperStats();
      fetchCasperLogs();
      fetchCasperAssignments();
    } catch (error) {
      console.error('Error processing items:', error);
      const errorMessage = error.response?.data?.error || 'Failed to process items';
      modal.error(errorMessage);
    } finally {
      setCasperProcessing(false);
    }
  };

  const handleProcessBrand = async () => {
    if (!selectedBrand) {
      modal.error('Please select a brand to process');
      return;
    }

    try {
      setCasperProcessing(true);
      const response = await axios.post('/api/casper/process', { brand_id: selectedBrand });

      if (response.data.processed > 0) {
        modal.success(`Successfully processed ${response.data.processed} items! (${response.data.ideas_processed || 0} ideas, ${response.data.drafts_processed || 0} drafts)`);
      } else {
        modal.info('No pending items to process for this brand');
      }

      // Refresh stats, logs, and assignments
      fetchCasperStats();
      fetchCasperLogs();
      fetchCasperAssignments();
    } catch (error) {
      console.error('Error processing brand:', error);
      const errorMessage = error.response?.data?.error || 'Failed to process brand';
      modal.error(errorMessage);
    } finally {
      setCasperProcessing(false);
    }
  };

  const handleEnableCasper = async () => {
    try {
      setCasperLoading(true);
      const response = await axios.post('/api/super-admin/casper/enable');

      if (response.data.success) {
        modal.success('Casper bot enabled successfully!');

        // Update Casper status instantly
        const casperUser = response.data.casper_user;
        setCasperStatus({
          enabled: true,
          casper_user: casperUser
        });

        // Add Casper to clients list instantly
        setDashboardData(prev => {
          if (!prev?.data?.clients) return prev;

          // Check if Casper already exists in clients list
          const casperExists = prev.data.clients.some(c => c._id === casperUser._id);
          if (casperExists) return prev;

          return {
            ...prev,
            data: {
              ...prev.data,
              clients: [...prev.data.clients, {
                _id: casperUser._id,
                id: casperUser._id,
                name: casperUser.name,
                email: casperUser.email,
                entity_type: casperUser.entity_type,
                is_bot: casperUser.is_bot,
                is_casper: true,
                agency_id: casperUser.agency_id
              }]
            }
          };
        });

        // Fetch actual stats and assignments to show historical data
        fetchCasperStats();
        fetchCasperAssignments();
      }
    } catch (error) {
      console.error('Error enabling Casper:', error);
      const errorMessage = error.response?.data?.error || 'Failed to enable Casper';
      modal.error(errorMessage);
    } finally {
      setCasperLoading(false);
    }
  };

  const handleDisableCasper = async () => {
    const result = await modal.confirm(
      'Are you sure you want to disable Casper? This will stop Casper from responding to new items and remove all assignments.',
      'Disable Casper Bot'
    );
    if (!result) {
      return;
    }

    try {
      setCasperLoading(true);
      const response = await axios.post('/api/super-admin/casper/disable');

      if (response.data.success) {
        modal.success('Casper bot disabled successfully!');

        // Update Casper status instantly
        setCasperStatus({
          enabled: false,
          casper_user: null
        });

        // Remove Casper from clients list instantly
        setDashboardData(prev => {
          if (!prev?.data?.clients) return prev;

          return {
            ...prev,
            data: {
              ...prev.data,
              clients: prev.data.clients.filter(c => !c.is_casper)
            }
          };
        });

        // Keep stats persistent - fetch from API to show historical data
        fetchCasperStats();
        fetchCasperAssignments();
      }
    } catch (error) {
      console.error('Error disabling Casper:', error);
      const errorMessage = error.response?.data?.error || 'Failed to disable Casper';
      modal.error(errorMessage);
    } finally {
      setCasperLoading(false);
    }
  };

  const fetchCalendarEvents = async () => {
    try {
      setCalendarLoading(true);
      setCalendarError(null);

      const now = new Date();
      const timeMin = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const timeMax = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();

      const response = await axios.get('/api/calendar/events', {
        params: { timeMin, timeMax, maxResults: 100 }
      });

      if (response.data.success) {
        setCalendarEvents(response.data.events || []);
      } else {
        setCalendarError(response.data.message || 'Failed to load calendar events');
      }
    } catch (error) {
      console.error('Calendar error:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to load calendar events. Please check server logs.';
      setCalendarError(errorMsg);
    } finally {
      setCalendarLoading(false);
    }
  };

  // Helper function to safely get string values
  const safeString = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
  };

  // Helper function to safely format dates
  const safeDate = (dateValue) => {
    if (!dateValue) return 'Never';
    try {
      if (typeof dateValue === 'string') {
        return new Date(dateValue).toLocaleDateString();
      }
      if (dateValue instanceof Date) {
        return dateValue.toLocaleDateString();
      }
      return 'Invalid Date';
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const handleSendInvitation = async (e) => {
    e.preventDefault();
    setInviteLoading(true);

    try {
      const user_role_data = {};

      // Get the current agency context from activeAgency or session
      const currentAgencyId = activeAgency?._id || sessionStorage.getItem('selected_agency_id');
      const currentAgencyName = activeAgency?.agency_name || sessionStorage.getItem('selectedAgencyName');

      if (inviteForm.user_type === 'client') {
        // For clients, use the selected organization/brand if provided, otherwise use agency context
        if (inviteForm.organization_id) {
          user_role_data.organization_id = inviteForm.organization_id;
        }
        if (inviteForm.brand_id) {
          user_role_data.brand_id = inviteForm.brand_id;
        }
        // Always include agency context for filtering
        if (currentAgencyId) {
          user_role_data.agency_id = currentAgencyId;
          user_role_data.agency_name = currentAgencyName || '';
        } else if (inviteForm.agency_name) {
          // Fallback to manual agency_name if no context
          user_role_data.agency_name = inviteForm.agency_name;
        }
      } else if (inviteForm.user_type === 'admin') {
        // Admins need agency context
        if (inviteForm.agency_id) {
          user_role_data.agency_id = inviteForm.agency_id;
          user_role_data.agency_name = inviteForm.agency_name || '';
        } else if (currentAgencyId) {
          user_role_data.agency_id = currentAgencyId;
          user_role_data.agency_name = currentAgencyName || '';
        }
      } else if (inviteForm.user_type === 'operator') {
        // Operators need agency context
        if (inviteForm.agency_id) {
          user_role_data.agency_id = inviteForm.agency_id;
          user_role_data.agency_name = inviteForm.agency_name || '';
        } else if (currentAgencyId) {
          user_role_data.agency_id = currentAgencyId;
          user_role_data.agency_name = currentAgencyName || '';
        }
      }

      const response = await axios.post('/api/super-admin/send-invitation', {
        full_name: inviteForm.full_name,
        email: inviteForm.email,
        user_type: inviteForm.user_type,
        user_role_data
      });
      
      if (response.data.success) {
        modal.success(`Invitation sent successfully to ${inviteForm.email}!`);
        setInviteForm({
          full_name: '',
          email: '',
          user_type: 'client',
          organization_id: '',
          brand_id: '',
          agency_id: '',
          agency_name: ''
        });
        fetchPendingInvitations(); // Refresh invitations list
      }
      
    } catch (error) {
      console.error('Error sending invitation:', error);
      const errorMessage = error.response?.data?.error || 'Failed to send invitation';
      modal.error(errorMessage);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationToken, email) => {
    const result = await modal.confirm(`Are you sure you want to cancel the invitation for ${email}?`, 'Cancel Invitation');
    if (!result) {
      return;
    }

    setCancellingInvitations(prev => new Set([...prev, invitationToken]));
    
    try {
      const response = await axios.post('/api/super-admin/cancel-invitation', {
        token: invitationToken
      });
      
      if (response.data.success) {
        modal.success('Invitation cancelled successfully!');
        fetchPendingInvitations(); // Refresh invitations list
      }

    } catch (error) {
      console.error('Error cancelling invitation:', error);
      const errorMessage = error.response?.data?.error || 'Failed to cancel invitation';
      modal.error(errorMessage);
    } finally {
      setCancellingInvitations(prev => {
        const newSet = new Set(prev);
        newSet.delete(invitationToken);
        return newSet;
      });
    }
  };

  const handleDeleteBrand = async (brandId, brandName) => {
    const result = await modal.confirm(`Are you sure you want to delete brand: ${brandName}? This action cannot be undone.`, 'Delete Brand');
    if (!result) {
      return;
    }

    try {
      const response = await axios.delete(`/api/super-admin/brands/${brandId}`, {
        withCredentials: true
      });

      if (response.data.success) {
        modal.success(`Brand "${brandName}" deleted successfully!`);
        fetchDashboardData(); // Refresh dashboard data
      }

    } catch (error) {
      console.error('Error deleting brand:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete brand';
      modal.error(errorMessage);
    }
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/super-admin/create-organization', organizationForm);
      setOrganizationForm({
        organization_name: '',
        owner_id: ''
      });
      modal.success('Organization created successfully!');
      setOrgBrandsActiveSection('overview');
      fetchDashboardData();
      // Refresh org selector to show new org
      setOrgRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error creating organization:', error);
      modal.error('Failed to create organization');
    }
  };

  const handleCreateBrand = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/super-admin/create-brand', brandForm);
      setBrandForm({
        brand_name: '',
        organization_id: '',
        owner_id: ''
      });
      modal.success('Brand created successfully!');
      setOrgBrandsActiveSection('overview');
      fetchDashboardData();
    } catch (error) {
      console.error('Error creating brand:', error);
      modal.error('Failed to create brand');
    }
  };


  const handleAgencyOnboard = async (e) => {
    e.preventDefault();

    // Validate that if one admin field is filled, both must be filled
    const hasAdminEmail = agencyOnboardForm.admin_email.trim() !== '';
    const hasAdminName = agencyOnboardForm.admin_name.trim() !== '';

    if (hasAdminEmail !== hasAdminName) {
      modal.warning('If providing admin details, both email and name are required');
      return;
    }

    try {
      setAgencyOnboardLoading(true);
      const response = await axios.post('/api/super-admin/onboard-agency', agencyOnboardForm);
      setAgencyOnboardForm({
        agency_name: '',
        admin_email: '',
        admin_name: '',
        plan: 'basic'
      });

      // Update success message based on whether admin was added
      if (hasAdminEmail) {
        modal.success(`Agency "${agencyOnboardForm.agency_name}" created successfully! Invitation sent to ${agencyOnboardForm.admin_email}`);
      } else {
        modal.success(`Agency "${agencyOnboardForm.agency_name}" created successfully! You can add admins later via user management.`);
      }

      fetchDashboardData();
    } catch (error) {
      console.error('Error onboarding agency:', error);
      const errorMsg = error.response?.data?.error || 'Failed to onboard agency';
      modal.error(errorMsg);
    } finally {
      setAgencyOnboardLoading(false);
    }
  };

  const handleEditOrganization = (org) => {
    setEditingOrganization(org);
    setEditOrgForm({
      organization_name: org.organization_name || org.name || '',
      owner_id: org.owner_id || '',
      icon_url: org.icon_url || ''
    });
  };

  const handleUpdateOrganization = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/super-admin/organizations/${editingOrganization._id}`, editOrgForm);
      setEditingOrganization(null);
      setEditOrgForm({
        organization_name: '',
        owner_id: ''
      });
      modal.success('Organization updated successfully!');
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating organization:', error);
      modal.error('Failed to update organization');
    }
  };

  const handleDeleteOrganization = async (org) => {
    const orgName = org.organization_name || org.name;
    const result = await modal.confirm(`Are you sure you want to delete the organization "${orgName}"?\n\nThis will also delete all brands under this organization. This action cannot be undone.`, 'Delete Organization');
    if (!result) {
      return;
    }

    try {
      const response = await axios.delete(`/api/super-admin/organizations/${org._id}`, {
        withCredentials: true
      });
      if (response.data.success) {
        modal.success(`Organization "${orgName}" and associated brands deleted successfully!`);
      } else {
        modal.success('Organization deleted successfully!');
      }
      fetchDashboardData();
      // Refresh org selector to remove deleted org
      setOrgRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting organization:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete organization';
      modal.error(errorMessage);
    }
  };

  const handleEditBrand = (brand) => {
    setEditingBrand(brand);
    setEditBrandForm({
      brand_name: brand.brand_name || brand.name,
      organization_id: brand.organization_id,
      owner_id: brand.owner_id || '',
      icon_url: brand.icon_url || ''
    });
  };

  const handleUpdateBrand = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/super-admin/brands/${editingBrand._id}`, editBrandForm);
      setEditingBrand(null);
      setEditBrandForm({
        brand_name: '',
        organization_id: '',
        owner_id: '',
        icon_url: ''
      });
      modal.success('Brand updated successfully!');
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating brand:', error);
      modal.error('Failed to update brand');
    }
  };

  const getContextMenuTitle = () => {
    switch (activeSection) {
      case 'dashboard':
        return 'Dashboard';
      case 'todo':
        return 'To Do List';
      case 'create':
        return 'Create Content';
      case 'calendar':
        return 'Meetings';
      case 'recordings':
        return 'Recordings';
      case 'velvet-recordings':
        return 'Velvet Recordings';
      case 'proxy-user':
        return 'Proxy User';
      case 'admin-user-management':
        return 'User Management';
      case 'admin-organizations-brands':
        return 'Organization';
      case 'prompt-hub':
        return 'Prompt Hub';
      case 'gemini':
        return 'Velvet AI';
      case 'create-ideas':
        return 'Create Ideas';
      case 'operator-review-ideas':
        return 'Review Ideas';
      case 'review-content':
        return 'Review Content';
      case 'schedule-posts':
        return 'Schedule Posts';
      case 'assets':
        return 'Brand Identity';
      case 'sources':
        return 'Sources Library';
      case 'activity-log':
        return 'Activity Logs';
      case 'logs':
        return 'System Logs';
      case 'create-agency':
        return 'Create Agency';
      case 'guest-completions':
        return 'Guest Completions';
      case 'casper':
        return 'Casper';
      case 'agency-hub':
        return 'Agency Hub';
      default:
        return 'Dashboard';
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <DashboardHome user={user} onSectionChange={setActiveSection} />;
      case 'todo':
        return <ToDoList user={user} onSectionChange={setActiveSection} />;
      case 'create':
        return <ContentCreation user={user} />;
      case 'sources':
        return <SourcesPanel user={user} />;
      case 'recordings':
        return <Recordings user={user} activeAgency={activeAgency} />;
      case 'velvet-recordings':
        return LIVEKIT_ENABLED
          ? <LiveKitRecordings user={user} activeAgency={activeAgency} />
          : <DashboardHome user={user} onSectionChange={setActiveSection} />;
      case 'calendar':
        return <MeetingsPanel user={user} />;
      case 'proxy-user':
        return <ProxyUser user={user} onLogout={onLogout} dashboardData={dashboardData} />;
      case 'admin-user-management':
        return renderUserManagementWithNav();
      case 'admin-organizations-brands':
        return (
          <OrganizationsBrandsPanel
            user={user}
            dashboardData={dashboardData}
            isSuperAdmin={true}
            onRefresh={refreshDashboardData}
          />
        );
      case 'prompt-hub':
        return <PromptHub user={user} />;
      case 'gemini':
        return <Gemini user={user} />;
      case 'create-ideas':
        return <CreateIdeas user={user} />;
      case 'operator-review-ideas':
        return <IdeasPanel user={user} />;
      case 'review-content':
        return <ReviewContentPanel user={user} />;
      case 'schedule-posts':
        return <SchedulePostsPanel user={user} />;
      case 'assets':
        return <BrandAssets user={user} />;
      case 'brand-builder':
        return <BrandVoiceDesigner user={user} />;
      case 'activity-log':
        return <ActivityLogPanel user={user} />;
      case 'logs':
        return <Logs user={user} />;
      case 'create-agency':
        return renderCreateAgency();
      case 'guest-completions':
        return renderGuestCompletions();
      case 'casper':
        return renderCasper();
      case 'agency-hub':
        return <AgencyHubPanel user={user} onRefresh={refreshDashboardData} />;
      case 'profile':
        return <ProfilePanel user={user} onUserUpdate={onUserUpdate} />;
      case 'notifications':
        return <NotificationsPanel user={user} />;
      default:
        return renderOverview();
    }
  };

  const renderDashboardWithInternalNav = () => (
    <div className="super-admin-internal-layout">
      <nav className="internal-nav">
        <div className="internal-nav-items">
          <button
            className={`internal-nav-btn ${activeSection === 'dashboard' && orgBrandsActiveSection === 'overview' ? 'active' : ''}`}
            onClick={() => {
              setActiveSection('dashboard');
              setOrgBrandsActiveSection('overview');
            }}
          >
            Overview
          </button>
          <button
            className={`internal-nav-btn ${activeSection === 'dashboard' && orgBrandsActiveSection === 'invitations' ? 'active' : ''}`}
            onClick={() => {
              setActiveSection('dashboard');
              setOrgBrandsActiveSection('invitations');
            }}
          >
            Invitations
          </button>
        </div>
      </nav>
      <div className="internal-content">
        {renderInternalContent()}
      </div>
    </div>
  );

  const renderInternalContent = () => {
    switch (orgBrandsActiveSection) {
      case 'overview':
        return renderOverview();
      case 'invitations':
        return renderInvitations();
      default:
        return renderOverview();
    }
  };

  const renderOrganizationsBrandsWithNav = () => (
    <div className="super-admin-internal-layout">
      <nav className="internal-nav">
        <div className="internal-nav-items">
          <button
            className={`internal-nav-btn ${orgBrandsSection === 'create-org' ? 'active' : ''}`}
            onClick={() => setOrgBrandsSection('create-org')}
          >
            Create Organization
          </button>
          <button
            className={`internal-nav-btn ${orgBrandsSection === 'create-brand' ? 'active' : ''}`}
            onClick={() => setOrgBrandsSection('create-brand')}
          >
            Create Brand
          </button>
          <button
            className={`internal-nav-btn ${orgBrandsSection === 'view-orgs' ? 'active' : ''}`}
            onClick={() => setOrgBrandsSection('view-orgs')}
          >
            Edit Organizations
          </button>
        </div>
      </nav>
      <div className="internal-content">
        {renderOrgBrandsSectionContent()}
      </div>
    </div>
  );

  const renderUserManagementWithNav = () => {
    const renderUserMgmtContent = () => {
      switch (userMgmtSection) {
        case 'management':
          return <ManagementPanel user={user} dashboardData={dashboardData} onRefresh={refreshDashboardData} />;
        case 'invitations':
        default:
          return (
            <InvitationsPanel
              user={user}
              dashboardData={dashboardData}
              isSuperAdmin={user?.is_super_admin || false}
              activeAgency={activeAgency}
            />
          );
      }
    };

    return (
      <div className="super-admin-internal-layout">
        <nav className="internal-nav">
          <div className="internal-nav-items">
            <button
              className={`internal-nav-btn ${userMgmtSection === 'invitations' ? 'active' : ''}`}
              onClick={() => setUserMgmtSection('invitations')}
              data-onboarding="invitations-tab"
            >
              Invitations
            </button>
            <button
              className={`internal-nav-btn ${userMgmtSection === 'management' ? 'active' : ''}`}
              onClick={() => setUserMgmtSection('management')}
              data-onboarding="management-tab"
            >
              Management
            </button>
          </div>
        </nav>
        <div className="internal-content">
          {renderUserMgmtContent()}
        </div>
      </div>
    );
  };


  const renderOrgBrandsSectionContent = () => {
    switch (orgBrandsSection) {
      case 'create-org':
        return (
          <div className="content-section">
            <h2>Create New Organization</h2>
            <div className="admin-card has-form">
              <form className="org-form" onSubmit={handleCreateOrganization}>
                <div className="form-group">
                  <label htmlFor="org-name">Organization Name *</label>
                  <input
                    type="text"
                    id="org-name"
                    value={organizationForm.organization_name}
                    onChange={(e) => setOrganizationForm({...organizationForm, organization_name: e.target.value})}
                    placeholder="Enter organization name"
                    required
                    autoComplete="off"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                  />
                </div>
                <div className="form-group">
                  <ImageUpload
                    value={organizationForm.icon_url}
                    onChange={(s3_url) => setOrganizationForm({...organizationForm, icon_url: s3_url})}
                    onClear={() => setOrganizationForm({...organizationForm, icon_url: ''})}
                    label="Organization Icon"
                    required={false}
                    maxSizeMB={5}
                    fileType="organization_icon"
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="modal-btn modal-btn-confirm">Create Organization</button>
                </div>
              </form>
            </div>
          </div>
        );
      case 'create-brand':
        return (
          <div className="content-section">
            <h2>Create New Brand</h2>
            <div className="admin-card has-form">
              <form className="brand-form" onSubmit={handleCreateBrand}>
                <div className="form-group">
                  <label htmlFor="brand-org">Parent Organization *</label>
                  <CustomSelect
                      id="brand-org"
                      value={brandForm.organization_id}
                      onChange={(e) => setBrandForm({...brandForm, organization_id: e.target.value})}
                      required
                    >
                      <option value="">Select Organization</option>
                      {dashboardData?.data?.organizations?.map(org => (
                        <option key={safeString(org._id || org.id)} value={safeString(org._id || org.id)}>
                          {safeString(org.organization_name || org.name)}
                        </option>
                      ))}
                    </CustomSelect>
                  <small>Brands must belong to an organization</small>
                </div>
                <div className="form-group">
                  <label htmlFor="brand-name">Brand Name *</label>
                  <input
                    type="text"
                    id="brand-name"
                    value={brandForm.brand_name}
                    onChange={(e) => setBrandForm({...brandForm, brand_name: e.target.value})}
                    placeholder="Enter brand name"
                    required
                    autoComplete="off"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                  />
                </div>
                <div className="form-group">
                  <ImageUpload
                    value={brandForm.icon_url}
                    onChange={(s3_url) => setBrandForm({...brandForm, icon_url: s3_url})}
                    onClear={() => setBrandForm({...brandForm, icon_url: ''})}
                    label="Brand Icon"
                    required={false}
                    maxSizeMB={5}
                    fileType="brand_icon"
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="modal-btn modal-btn-confirm">Create Brand</button>
                </div>
              </form>
            </div>
          </div>
        );
      case 'view-orgs':
        return (
          <div className="content-section">
            <h2>View All Organizations</h2>
            <div className="organizations-list">
              {dashboardData?.data?.organizations?.length > 0 ? (
                dashboardData.data.organizations.map(org => (
                  <div key={safeString(org._id)} className="organization-card">
                    <div className="org-header">
                      <div className="org-info">
                        <h4>{safeString(org.organization_name || org.name)}</h4>
                        <span className="org-owner">
                          Owner: {safeString(org.owner_name || 'Not assigned')}
                        </span>
                      </div>
                      <div className="org-actions">
                        <button
                          className="view-btn"
                          onClick={() => setSelectedOrganization(org)}
                        >
                          View Details
                        </button>
                        <button
                          className="edit-btn"
                          onClick={() => handleEditOrganization(org)}
                        >
                          Edit
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDeleteOrganization(org)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {/* Show brands for this organization */}
                    <div className="org-brands">
                      <h5>Brands ({
                        dashboardData?.data?.brands?.filter(brand =>
                          safeString(brand.organization_id) === safeString(org._id)
                        ).length || 0
                      })</h5>
                      <div className="brands-list">
                        {dashboardData?.data?.brands
                          ?.filter(brand => safeString(brand.organization_id) === safeString(org._id))
                          .map(brand => (
                            <div key={safeString(brand._id)} className="brand-item">
                              <span className="brand-name">{safeString(brand.brand_name || brand.name)}</span>
                              <span className="brand-owner">
                                Owner: {safeString(brand.owner_name || 'Not assigned')}
                              </span>
                              <div className="brand-actions">
                                <button
                                  className="edit-btn small"
                                  onClick={() => handleEditBrand(brand)}
                                >
                                  Edit
                                </button>
                                <button className="delete-btn small">Delete</button>
                              </div>
                            </div>
                          ))
                        }
                        {(!dashboardData?.data?.brands?.some(brand =>
                          safeString(brand.organization_id) === safeString(org._id)
                        )) && (
                          <p className="no-brands">No brands in this organization</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <p>No organizations found.</p>
                  <button
                    className="create-btn"
                    onClick={() => setOrgBrandsSection('create-org')}
                  >
                    Create Your First Organization
                  </button>
                </div>
              )}
            </div>

            {/* Organization Details Modal */}
            {selectedOrganization && (
              <div className="modal-overlay" onClick={() => setSelectedOrganization(null)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Organization Details</h3>
                    <button className="modal-close-btn" onClick={() => setSelectedOrganization(null)} aria-label="Close" />
                  </div>
                  <div className="modal-body">
                    <div className="detail-section">
                      <h4>Basic Information</h4>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <span className="detail-label">Name:</span>
                          <span className="detail-value">{selectedOrganization.organization_name || selectedOrganization.name}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Owner:</span>
                          <span className="detail-value">{selectedOrganization.owner_name || 'Not assigned'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Created:</span>
                          <span className="detail-value">{selectedOrganization.created_at ? new Date(selectedOrganization.created_at).toLocaleDateString() : 'Unknown'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Brands:</span>
                          <span className="detail-value">
                            {dashboardData?.data?.brands?.filter(brand =>
                              safeString(brand.organization_id) === safeString(selectedOrganization._id)
                            ).length || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="detail-section">
                      <h4>Brands in this Organization</h4>
                      <div className="brands-detail-list">
                        {dashboardData?.data?.brands
                          ?.filter(brand => safeString(brand.organization_id) === safeString(selectedOrganization._id))
                          .map(brand => (
                            <div key={safeString(brand._id)} className="brand-detail-item">
                              <span className="brand-detail-name">{safeString(brand.brand_name || brand.name)}</span>
                              <span className="brand-detail-owner">Owner: {safeString(brand.owner_name || 'Not assigned')}</span>
                            </div>
                          )) || <p>No brands in this organization</p>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Organization Modal */}
            {editingOrganization && (
              <div className="modal-overlay" onClick={() => setEditingOrganization(null)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Edit Organization</h3>
                    <button className="modal-close-btn" onClick={() => setEditingOrganization(null)} aria-label="Close" />
                  </div>
                  <div className="modal-body">
                    <form onSubmit={handleUpdateOrganization}>
                      <div className="form-group">
                        <label htmlFor="edit-org-name">Organization Name *</label>
                        <input
                          type="text"
                          id="edit-org-name"
                          value={editOrgForm.organization_name}
                          onChange={(e) => setEditOrgForm({...editOrgForm, organization_name: e.target.value})}
                          placeholder="Enter organization name"
                          required
                          autoComplete="off"
                          data-1p-ignore="true"
                          data-lpignore="true"
                          data-bwignore="true"
                          data-form-type="other"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="edit-org-owner">Client Owner</label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <CustomSelect
                              id="edit-org-owner"
                              value={editOrgForm.owner_id}
                              onChange={(e) => setEditOrgForm({...editOrgForm, owner_id: e.target.value})}
                            >
                              <option value="" disabled hidden>Select Client</option>
                              {dashboardData?.data?.clients?.map(client => (
                                <option key={safeString(client._id || client.id)} value={safeString(client._id || client.id)}>
                                  {safeString(client.name)} ({safeString(client.email)})
                                </option>
                              ))}
                            </CustomSelect>
                          {editOrgForm.owner_id && (
                            <button
                              type="button"
                              onClick={() => setEditOrgForm({...editOrgForm, owner_id: ''})}
                              style={{
                                backgroundColor: 'var(--color-danger-button)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                width: '32px',
                                height: '32px',
                                cursor: 'pointer',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}
                              title="Remove owner"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="form-group">
                        <ImageUpload
                          value={editOrgForm.icon_url}
                          onChange={(base64) => setEditOrgForm({...editOrgForm, icon_url: base64})}
                          onClear={() => setEditOrgForm({...editOrgForm, icon_url: ''})}
                          label="Organization Icon"
                          required={false}
                          maxSizeMB={5}
                        />
                      </div>
                      <div className="form-actions">
                        <button type="submit" className="modal-btn modal-btn-confirm">Update Organization</button>
                        <button
                          type="button"
                          className="modal-btn modal-btn-cancel"
                          onClick={() => setEditingOrganization(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Brand Modal */}
            {editingBrand && (
              <div className="modal-overlay" onClick={() => setEditingBrand(null)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Edit Brand</h3>
                    <button className="modal-close-btn" onClick={() => setEditingBrand(null)} aria-label="Close" />
                  </div>
                  <div className="modal-body">
                    <form onSubmit={handleUpdateBrand}>
                      <div className="form-group">
                        <label htmlFor="edit-brand-name">Brand Name *</label>
                        <input
                          type="text"
                          id="edit-brand-name"
                          value={editBrandForm.brand_name}
                          onChange={(e) => setEditBrandForm({...editBrandForm, brand_name: e.target.value})}
                          placeholder="Enter brand name"
                          required
                          autoComplete="off"
                          data-1p-ignore="true"
                          data-lpignore="true"
                          data-bwignore="true"
                          data-form-type="other"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="edit-brand-org">Parent Organization *</label>
                        <CustomSelect
                            id="edit-brand-org"
                            value={editBrandForm.organization_id}
                            onChange={(e) => setEditBrandForm({...editBrandForm, organization_id: e.target.value})}
                            required
                          >
                            <option value="">Select Organization</option>
                            {dashboardData?.data?.organizations?.map(org => (
                              <option key={safeString(org._id || org.id)} value={safeString(org._id || org.id)}>
                                {safeString(org.organization_name || org.name)}
                              </option>
                            ))}
                          </CustomSelect>
                      </div>
                      <div className="form-group">
                        <label htmlFor="edit-brand-owner">Client Owner</label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <CustomSelect
                              id="edit-brand-owner"
                              value={editBrandForm.owner_id}
                              onChange={(e) => setEditBrandForm({...editBrandForm, owner_id: e.target.value})}
                            >
                              <option value="" disabled hidden>Select Client</option>
                              {dashboardData?.data?.clients?.map(client => (
                                <option key={safeString(client._id || client.id)} value={safeString(client._id || client.id)}>
                                  {safeString(client.name)} ({safeString(client.email)})
                                </option>
                              ))}
                            </CustomSelect>
                          {editBrandForm.owner_id && (
                            <button
                              type="button"
                              onClick={() => setEditBrandForm({...editBrandForm, owner_id: ''})}
                              style={{
                                backgroundColor: 'var(--color-danger-button)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                width: '32px',
                                height: '32px',
                                cursor: 'pointer',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}
                              title="Remove owner"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="form-group">
                        <ImageUpload
                          value={editBrandForm.icon_url}
                          onChange={(base64) => setEditBrandForm({...editBrandForm, icon_url: base64})}
                          onClear={() => setEditBrandForm({...editBrandForm, icon_url: ''})}
                          label="Brand Icon"
                          required={false}
                          maxSizeMB={5}
                        />
                      </div>
                      <div className="form-actions">
                        <button type="submit" className="modal-btn modal-btn-confirm">Update Brand</button>
                        <button
                          type="button"
                          className="modal-btn modal-btn-cancel"
                          onClick={() => setEditingBrand(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return (
          <div className="content-section">
            <h2>Create New Organization</h2>
            <p>Select a section from the navigation above.</p>
          </div>
        );
    }
  };

  const renderCasper = () => {
    // Check if an agency is selected
    const hasAgencySelected = activeAgency && activeAgency._id;

    const renderBotPanel = () => (
      <div className="casper-bot-content">
        {!hasAgencySelected ? (
          <div className="casper-no-agency">
            <p>Please select an agency from the dropdown in the left navigation to manage Casper Bot settings.</p>
          </div>
        ) : casperLoading ? (
          <div className="casper-loading">
            <div className="casper-spinner"></div>
            <span>Loading Casper status...</span>
          </div>
        ) : (
          <>
            <div className="casper-status-card">
              <h4>Bot Status</h4>
              <div className="casper-status-grid">
                <div className="casper-status-item">
                  <span className="casper-status-label">Status</span>
                  <span className={`casper-status-badge ${casperStatus?.enabled ? 'enabled' : 'disabled'}`}>
                    {casperStatus?.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                {casperStatus?.casper_user && (
                  <>
                    <div className="casper-status-item">
                      <span className="casper-status-label">Bot Name</span>
                      <span className="casper-status-value">{casperStatus.casper_user.name}</span>
                    </div>
                    <div className="casper-status-item">
                      <span className="casper-status-label">Bot Email</span>
                      <span className="casper-status-value">{casperStatus.casper_user.email}</span>
                    </div>
                    <div className="casper-status-item">
                      <span className="casper-status-label">User Type</span>
                      <span className="casper-status-value">{casperStatus.casper_user.entity_type}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="casper-actions">
                <div className="casper-toggle-container">
                  <span className="casper-toggle-label">Enable Casper Bot</span>
                  <label className="casper-toggle-switch">
                    <input
                      type="checkbox"
                      checked={casperStatus?.enabled || false}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleEnableCasper();
                        } else {
                          handleDisableCasper();
                        }
                      }}
                      disabled={casperLoading}
                    />
                    <span className="casper-toggle-slider"></span>
                  </label>
                </div>
                <button
                  className="casper-btn casper-btn-primary"
                  onClick={handleProcessAllPending}
                  disabled={casperProcessing || !casperStatus?.enabled}
                >
                  {casperProcessing ? 'Processing...' : 'Process All Pending'}
                </button>
              </div>
            </div>

            {/* Activity Stats Dashboard */}
            <div className="casper-stats-card">
                <h4>Activity Stats</h4>
                {casperStatsLoading ? (
                  <div className="casper-loading">
                    <div className="casper-spinner"></div>
                    <span>Loading stats...</span>
                  </div>
                ) : (
                  <>
                    <div className="casper-stats-grid">
                      <div className="casper-stat-item">
                        <span className="casper-stat-value">{casperStats?.total_processed || 0}</span>
                        <span className="casper-stat-label">Total Processed</span>
                      </div>
                      <div className="casper-stat-item casper-stat-approved">
                        <span className="casper-stat-value">{casperStats?.approved || 0}</span>
                        <span className="casper-stat-label">Approved</span>
                      </div>
                      <div className="casper-stat-item casper-stat-denied">
                        <span className="casper-stat-value">{casperStats?.denied || 0}</span>
                        <span className="casper-stat-label">Denied</span>
                      </div>
                      <div className="casper-stat-item casper-stat-revision">
                        <span className="casper-stat-value">{casperStats?.revision_requested || 0}</span>
                        <span className="casper-stat-label">Revisions</span>
                      </div>
                      <div className="casper-stat-item casper-stat-pending">
                        <span className="casper-stat-value">{casperStats?.pending_count || 0}</span>
                        <span className="casper-stat-label">Pending</span>
                      </div>
                    </div>
                    {casperStats?.last_activity && (
                      <div className="casper-last-activity">
                        Last activity: {new Date(casperStats.last_activity).toLocaleString()}
                      </div>
                    )}
                  </>
                )}
              </div>

            {/* Current Assignments */}
            <div className="casper-assignments-card">
                <h4>Current Assignments</h4>
                {casperAssignmentsLoading ? (
                  <div className="casper-loading">
                    <div className="casper-spinner"></div>
                    <span>Loading assignments...</span>
                  </div>
                ) : casperAssignments.length === 0 ? (
                  <div className="casper-empty">
                    <p>Casper is not assigned to any brands yet.</p>
                    <p>Assign Casper to a brand in the Organizations & Brands panel to get started.</p>
                  </div>
                ) : (
                  <div className="casper-assignments-list">
                    {casperAssignments.map((assignment) => (
                      <div key={assignment._id} className="casper-assignment-item">
                        {assignment.icon_url && (
                          <div className="casper-assignment-logo">
                            <BrandIcon src={assignment.icon_url} alt={assignment.name} />
                          </div>
                        )}
                        <div className="casper-assignment-brand">
                          <span className="casper-assignment-org">{assignment.organization_name}</span>
                          <span className="casper-assignment-name">{assignment.name}</span>
                        </div>
                        <div className="casper-assignment-stats">
                          {assignment.pending_total > 0 ? (
                            <>
                              <span className="casper-assignment-pending">
                                {assignment.pending_total} pending
                              </span>
                              <span className="casper-assignment-detail">
                                ({assignment.pending_ideas} ideas, {assignment.pending_drafts} drafts)
                              </span>
                            </>
                          ) : (
                            <span className="casper-assignment-none">No pending items</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
          </>
        )}
      </div>
    );

    const renderLogsPanel = () => {
      const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric'
        });
      };

      const formatFullDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: 'numeric', minute: '2-digit', hour12: true
        });
      };

      const ACTION_COLORS = {
        approved: '#A4E0C0',
        denied: 'var(--color-error)',
        revision_requested: '#8b5cf6',
      };

      const ACTION_BADGE_COLORS = {
        approved: '#10B981',
        denied: 'var(--color-error)',
        revision_requested: '#8b5cf6',
      };

      const ENTITY_LABELS = {
        idea: 'Idea',
        content_draft: 'Content',
      };

      const ENTITY_COLORS = {
        idea: '#8B5CF6',
        content_draft: '#299DDB',
      };

      return (
        <div className="al-panel">
          <div className="al-header">
            <div className="al-header-title">
              <Icon name="robot" size="1.25rem" />
              <h3>Casper Activity Logs</h3>
            </div>
            <p className="al-subtitle">Track Casper's automated responses to ideas and content drafts.</p>
          </div>

          {!hasAgencySelected ? (
            <div className="al-empty">
              <Icon name="building" size="2rem" />
              <p>Select an agency to view Casper activity.</p>
            </div>
          ) : casperLogsLoading ? (
            <div className="al-loading">
              <div className="al-spinner"></div>
              <span>Loading activity...</span>
            </div>
          ) : casperLogs.length === 0 ? (
            <div className="al-empty">
              <Icon name="robot" size="2rem" />
              <p>No Casper activity yet.</p>
              <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>
                Casper logs will appear here after processing ideas or content drafts.
              </p>
            </div>
          ) : (
            <>
              <div className="al-feed">
                {casperLogs.map((log) => {
                  const actionColor = ACTION_COLORS[log.action] || '#9CA3AF';
                  const actionBadgeColor = ACTION_BADGE_COLORS[log.action] || '#9CA3AF';
                  const entityLabel = ENTITY_LABELS[log.item_type] || log.item_type;
                  const entityColor = ENTITY_COLORS[log.item_type] || '#8B5CF6';

                  return (
                    <div key={log._id} className="al-entry">
                      <div
                        className="al-avatar"
                        style={{ backgroundColor: '#6B7280' }}
                        title="Casper Bot"
                      >
                        🤖
                      </div>
                      <div className="al-entry-body">
                        <div className="al-entry-main">
                          <span className="al-name">Casper</span>
                          <span
                            className="al-role-badge"
                            style={{ backgroundColor: actionBadgeColor, color: '#ffffff' }}
                          >
                            {log.action_display}
                          </span>
                          <span className="al-action-summary">
                            {entityLabel}
                          </span>
                        </div>
                        <div className="al-entry-meta">
                          <span
                            className="al-entity-chip"
                            style={{
                              backgroundColor: entityColor,
                              borderColor: entityColor,
                              color: '#ffffff'
                            }}
                          >
                            {entityLabel}
                          </span>
                          {log.item_title && (
                            <span className="al-entity-title">{log.item_title}</span>
                          )}
                          <span
                            className="al-time"
                            title={formatFullDate(log.created_at)}
                          >
                            {formatDate(log.created_at)}
                          </span>
                        </div>
                        {log.comment && (
                          <div
                            className="al-comment-preview"
                            style={{
                              backgroundColor: `${actionColor}22`,
                              borderLeftColor: actionColor
                            }}
                          >
                            <Icon name="comment-empty" size="0.875rem" />
                            <span>"{log.comment}"</span>
                          </div>
                        )}
                      </div>
                      <div
                        className="al-action-indicator"
                        style={{ backgroundColor: actionColor }}
                        title={log.action_display}
                      ></div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      );
    };

    return (
      <div className="super-admin-internal-layout">
        <nav className="internal-nav">
          <div className="internal-nav-items">
            <button
              className={`internal-nav-btn ${casperActiveTab === 'bot' ? 'active' : ''}`}
              onClick={() => setCasperActiveTab('bot')}
            >
              Casper Bot
            </button>
            <button
              className={`internal-nav-btn ${casperActiveTab === 'logs' ? 'active' : ''}`}
              onClick={() => setCasperActiveTab('logs')}
            >
              Casper Logs
            </button>
          </div>
        </nav>

        <div className="internal-content casper-panel">
          {casperActiveTab === 'bot' ? renderBotPanel() : renderLogsPanel()}
        </div>
      </div>
    );
  };

  const renderCreateAgency = () => {
    const handleCreateAgency = async (e) => {
      e.preventDefault();
      if (!newAgencyForm.agency_name.trim()) {
        modal.warning('Agency name is required');
        return;
      }
      try {
        await axios.post('/api/super-admin/onboard-agency', { agency_name: newAgencyForm.agency_name.trim() });
        modal.success(`Agency "${newAgencyForm.agency_name}" created successfully!`);
        setNewAgencyForm({ agency_name: '' });
        fetchDashboardData();
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Failed to create agency';
        modal.error(errorMsg);
      }
    };

    return (
      <div className="content-section">
        <div className="admin-card has-form">
          <form className="agency-form" onSubmit={handleCreateAgency}>
            <div className="form-group">
              <label htmlFor="agency-name">Agency Name *</label>
              <input
                type="text"
                id="agency-name"
                value={newAgencyForm.agency_name}
                onChange={(e) => setNewAgencyForm({...newAgencyForm, agency_name: e.target.value})}
                placeholder="Enter agency name"
                required
                autoComplete="off"
                data-1p-ignore="true"
                data-lpignore="true"
                data-bwignore="true"
                data-form-type="other"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="button-main">Create Agency</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderGuestCompletions = () => {
    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    };

    return (
      <div className="gc-panel">
        {guestCompletionsLoading ? (
          <div className="gc-loading">
            <div className="gc-spinner"></div>
            <span>Loading guest completions...</span>
          </div>
        ) : guestCompletions.length === 0 ? (
          <div className="gc-empty">
            <p>No guest completions yet.</p>
            <p className="gc-empty-hint">
              Share the link <strong>myvelvet.app/guest</strong> to start collecting leads!
            </p>
          </div>
        ) : (
          <div className="gc-list">
            {guestCompletions.map((completion) => {
              const personaName = completion.persona_data?.name || 'Unknown';
              const voiceType = completion.final_winner || 'Unknown';

              // Map avatar ID to voice type number
              const voiceTypeMapping = {
                'A': '1', 'Ab': '1-2', 'B': '2', 'Bc': '2-3', 'C': '3', 'Cd': '3-4',
                'D': '4', 'De': '4-5', 'E': '5', 'Ef': '5-6', 'F': '6', 'Fg': '6-7',
                'G': '7', 'Gh': '7-8', 'H': '8', 'Hi': '8-9', 'I': '9'
              };
              const voiceTypeNumber = voiceTypeMapping[voiceType] || '?';

              return (
                <div key={completion._id} className="gc-entry">
                  <div className="gc-entry-main">
                    <div className="gc-entry-col gc-col-email">
                      <span className="gc-label">Email</span>
                      <span className="gc-value gc-email">{completion.guest_email}</span>
                    </div>
                    <div className="gc-entry-col gc-col-name">
                      <span className="gc-label">Name</span>
                      <span className="gc-value">{completion.guest_name || 'N/A'}</span>
                    </div>
                    <div className="gc-entry-col gc-col-brand">
                      <span className="gc-label">Brand</span>
                      <span className="gc-value">{completion.brand_name || 'N/A'}</span>
                    </div>
                    <div className="gc-entry-col gc-col-url">
                      <span className="gc-label">Website</span>
                      {completion.brand_url ? (
                        <a
                          href={completion.brand_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="gc-link"
                        >
                          {completion.brand_url.length > 40
                            ? completion.brand_url.substring(0, 40) + '...'
                            : completion.brand_url}
                        </a>
                      ) : (
                        <span className="gc-value">N/A</span>
                      )}
                    </div>
                    <div className="gc-entry-col gc-col-persona">
                      <span className="gc-label">Voice Type</span>
                      <span className="gc-voice-badge">Type {voiceTypeNumber}</span>
                    </div>
                    <div className="gc-entry-col gc-col-date">
                      <span className="gc-label">Completed</span>
                      <span className="gc-value gc-date">{formatDate(completion.completed_at)}</span>
                    </div>
                    <div className="gc-entry-col gc-col-count">
                      <span className="gc-label">Attempts</span>
                      <span className="gc-value">{completion.completion_count || 1}</span>
                    </div>
                    <div className="gc-entry-col gc-col-actions">
                      <button
                        className="delete-btn-inline"
                        onClick={() => handleDeleteGuestCompletion(completion.guest_email, completion.guest_name)}
                        disabled={deletingGuestCompletions.has(completion.guest_email)}
                        title="Delete this guest completion"
                      >
                        {deletingGuestCompletions.has(completion.guest_email) ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderOrganizationsBrands = () => (
    <div className="content-section">
      <h2>Organizations & Brands Control Panel</h2>

      {/* Control Panel Navigation */}
      <div className="control-panel-nav">
        <button
          className={`control-btn ${orgBrandsActiveSection === 'overview' ? 'active' : ''}`}
          onClick={() => setOrgBrandsActiveSection('overview')}
        >
          <Icon name="chart-bar" size="1rem" /> Overview
        </button>
        <button
          className={`control-btn ${orgBrandsActiveSection === 'create-org' ? 'active' : ''}`}
          onClick={() => setOrgBrandsActiveSection('create-org')}
        >
          <Icon name="building" size="1rem" /> Create Organization
        </button>
        <button
          className={`control-btn ${orgBrandsActiveSection === 'create-brand' ? 'active' : ''}`}
          onClick={() => setOrgBrandsActiveSection('create-brand')}
        >
          <Icon name="tag" size="1rem" /> Create Brand
        </button>
        <button
          className={`control-btn ${orgBrandsActiveSection === 'view-orgs' ? 'active' : ''}`}
          onClick={() => setOrgBrandsActiveSection('view-orgs')}
        >
          <Icon name="docs" size="1rem" /> View Organizations
        </button>
      </div>

      {/* Control Panel Content */}
      <div className="control-panel-content">
        {orgBrandsActiveSection === 'overview' && (
          <div className="overview-section">
            <h3>Organizations & Brands Overview</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-number">{dashboardData?.data?.organizations?.length || 0}</div>
                <div className="stat-label">Total Organizations</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{dashboardData?.data?.brands?.length || 0}</div>
                <div className="stat-label">Total Brands</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{dashboardData?.data?.operators?.length || 0}</div>
                <div className="stat-label">Operators</div>
              </div>
            </div>

          </div>
        )}

        {orgBrandsActiveSection === 'create-org' && (
          <div className="create-section">
            <h3>Create New Organization</h3>
            <div className="admin-card">
              <form className="org-form" onSubmit={handleCreateOrganization}>
                <div className="form-group">
                  <label htmlFor="org-name">Organization Name *</label>
                  <input
                    type="text"
                    id="org-name"
                    value={organizationForm.organization_name}
                    onChange={(e) => setOrganizationForm({...organizationForm, organization_name: e.target.value})}
                    placeholder="Enter organization name"
                    required
                    autoComplete="off"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="modal-btn modal-btn-confirm">Create Organization</button>
                  <button
                    type="button"
                    className="modal-btn modal-btn-cancel"
                    onClick={() => setOrgBrandsActiveSection('overview')}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {orgBrandsActiveSection === 'create-brand' && (
          <div className="create-section">
            <h3>Create New Brand</h3>
            <div className="admin-card">
              <form className="brand-form" onSubmit={handleCreateBrand}>
                <div className="form-group">
                  <label htmlFor="brand-org">Parent Organization *</label>
                  <CustomSelect
                      id="brand-org"
                      value={brandForm.organization_id}
                      onChange={(e) => setBrandForm({...brandForm, organization_id: e.target.value})}
                      required
                    >
                      <option value="">Select Organization</option>
                      {dashboardData?.data?.organizations?.map(org => (
                        <option key={safeString(org._id || org.id)} value={safeString(org._id || org.id)}>
                          {safeString(org.organization_name || org.name)}
                        </option>
                      ))}
                    </CustomSelect>
                  <small>Brands must belong to an organization</small>
                </div>
                <div className="form-group">
                  <label htmlFor="brand-name">Brand Name *</label>
                  <input
                    type="text"
                    id="brand-name"
                    value={brandForm.brand_name}
                    onChange={(e) => setBrandForm({...brandForm, brand_name: e.target.value})}
                    placeholder="Enter brand name"
                    required
                    autoComplete="off"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="modal-btn modal-btn-confirm">Create Brand</button>
                  <button
                    type="button"
                    className="modal-btn modal-btn-cancel"
                    onClick={() => setOrgBrandsActiveSection('overview')}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {orgBrandsActiveSection === 'view-orgs' && (
          <div className="view-section">
            <h3>Organizations & Brands</h3>
            <div className="organizations-list">
              {dashboardData?.data?.organizations?.length > 0 ? (
                dashboardData.data.organizations.map(org => (
                  <div key={safeString(org._id)} className="organization-card">
                    <div className="org-header">
                      <div className="org-info">
                        <h4>{safeString(org.organization_name || org.name)}</h4>
                        <span className="org-owner">
                          Owner: {safeString(org.owner_name || 'Not assigned')}
                        </span>
                      </div>
                      <div className="org-actions">
                        <button
                          className="view-btn"
                          onClick={() => setSelectedOrganization(org)}
                        >
                          View Details
                        </button>
                        <button
                          className="edit-btn"
                          onClick={() => handleEditOrganization(org)}
                        >
                          Edit
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDeleteOrganization(org)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {/* Show brands for this organization */}
                    <div className="org-brands">
                      <h5>Brands ({
                        dashboardData?.data?.brands?.filter(brand =>
                          safeString(brand.organization_id) === safeString(org._id)
                        ).length || 0
                      })</h5>
                      <div className="brands-list">
                        {dashboardData?.data?.brands
                          ?.filter(brand => safeString(brand.organization_id) === safeString(org._id))
                          .map(brand => (
                            <div key={safeString(brand._id)} className="brand-item">
                              <span className="brand-name">{safeString(brand.brand_name || brand.name)}</span>
                              <span className="brand-owner">
                                Owner: {safeString(brand.owner_name || 'Not assigned')}
                              </span>
                              <div className="brand-actions">
                                <button
                                  className="edit-btn small"
                                  onClick={() => handleEditBrand(brand)}
                                >
                                  Edit
                                </button>
                                <button className="delete-btn small">Delete</button>
                              </div>
                            </div>
                          ))
                        }
                        {(!dashboardData?.data?.brands?.some(brand =>
                          safeString(brand.organization_id) === safeString(org._id)
                        )) && (
                          <p className="no-brands">No brands in this organization</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <p>No organizations found.</p>
                  <button
                    className="create-btn"
                    onClick={() => setOrgBrandsActiveSection('create-org')}
                  >
                    Create Your First Organization
                  </button>
                </div>
              )}
            </div>

            {/* Organization Details Modal */}
            {selectedOrganization && (
              <div className="modal-overlay" onClick={() => setSelectedOrganization(null)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Organization Details</h3>
                    <button className="modal-close-btn" onClick={() => setSelectedOrganization(null)} aria-label="Close" />
                  </div>
                  <div className="modal-body">
                    <div className="detail-section">
                      <h4>Basic Information</h4>
                      <div className="detail-grid">
                        <div className="detail-item">
                          <span className="detail-label">Name:</span>
                          <span className="detail-value">{selectedOrganization.organization_name || selectedOrganization.name}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Owner:</span>
                          <span className="detail-value">{selectedOrganization.owner_name || 'Not assigned'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Created:</span>
                          <span className="detail-value">{selectedOrganization.created_at ? new Date(selectedOrganization.created_at).toLocaleDateString() : 'Unknown'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Brands:</span>
                          <span className="detail-value">
                            {dashboardData?.data?.brands?.filter(brand =>
                              safeString(brand.organization_id) === safeString(selectedOrganization._id)
                            ).length || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="detail-section">
                      <h4>Brands in this Organization</h4>
                      <div className="brands-detail-list">
                        {dashboardData?.data?.brands
                          ?.filter(brand => safeString(brand.organization_id) === safeString(selectedOrganization._id))
                          .map(brand => (
                            <div key={safeString(brand._id)} className="brand-detail-item">
                              <span className="brand-detail-name">{safeString(brand.brand_name || brand.name)}</span>
                              <span className="brand-detail-owner">Owner: {safeString(brand.owner_name || 'Not assigned')}</span>
                            </div>
                          )) || <p>No brands in this organization</p>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Organization Modal */}
            {editingOrganization && (
              <div className="modal-overlay" onClick={() => setEditingOrganization(null)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">Edit Organization</h3>
                    <button className="modal-close-btn" onClick={() => setEditingOrganization(null)} aria-label="Close" />
                  </div>
                  <div className="modal-body">
                    <form onSubmit={handleUpdateOrganization}>
                      <div className="form-group">
                        <label htmlFor="edit-org-name">Organization Name *</label>
                        <input
                          type="text"
                          id="edit-org-name"
                          value={editOrgForm.organization_name}
                          onChange={(e) => setEditOrgForm({...editOrgForm, organization_name: e.target.value})}
                          placeholder="Enter organization name"
                          required
                          autoComplete="off"
                          data-1p-ignore="true"
                          data-lpignore="true"
                          data-bwignore="true"
                          data-form-type="other"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="edit-org-owner">Client Owner</label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <CustomSelect
                              id="edit-org-owner"
                              value={editOrgForm.owner_id}
                              onChange={(e) => setEditOrgForm({...editOrgForm, owner_id: e.target.value})}
                            >
                              <option value="" disabled hidden>Select Client</option>
                              {dashboardData?.data?.clients?.map(client => (
                                <option key={safeString(client._id || client.id)} value={safeString(client._id || client.id)}>
                                  {safeString(client.name)} ({safeString(client.email)})
                                </option>
                              ))}
                            </CustomSelect>
                          {editOrgForm.owner_id && (
                            <button
                              type="button"
                              onClick={() => setEditOrgForm({...editOrgForm, owner_id: ''})}
                              style={{
                                backgroundColor: 'var(--color-danger-button)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                width: '32px',
                                height: '32px',
                                cursor: 'pointer',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}
                              title="Remove owner"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="form-group">
                        <ImageUpload
                          value={editOrgForm.icon_url}
                          onChange={(base64) => setEditOrgForm({...editOrgForm, icon_url: base64})}
                          onClear={() => setEditOrgForm({...editOrgForm, icon_url: ''})}
                          label="Organization Icon"
                          required={false}
                          maxSizeMB={5}
                        />
                      </div>
                      <div className="form-actions">
                        <button type="submit" className="modal-btn modal-btn-confirm">Update Organization</button>
                        <button
                          type="button"
                          className="modal-btn modal-btn-cancel"
                          onClick={() => setEditingOrganization(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderOverview = () => (
    <div className="content-section">
      <h2>System Overview</h2>
      <div className="admin-grid">
        <div className="admin-card overview-card">
          <h3>System Stats</h3>
          {dashboardData && (
            <div className="overview-stats">
              <div className="stat-item">
                <span className="stat-number">{dashboardData.data?.total_users || 0}</span>
                <span className="stat-label">Total Users</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{dashboardData.data?.admins?.length || 0}</span>
                <span className="stat-label">Admins</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{dashboardData.data?.operators?.length || 0}</span>
                <span className="stat-label">Operators</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{dashboardData.data?.clients?.length || 0}</span>
                <span className="stat-label">Clients</span>
              </div>
            </div>
          )}
        </div>

        <div className="admin-card">
          <h3>System Status</h3>
          <div className="quick-stats">
            <div className="stat-row">
              <span className="stat-name">Database:</span>
              <span className="stat-value connected">Connected</span>
            </div>
            <div className="stat-row">
              <span className="stat-name">Authentication:</span>
              <span className="stat-value">Active</span>
            </div>
          </div>
        </div>

        <div className="admin-card">
          <h3>Recent Activity</h3>
          <div className="activity-log">
            <div className="activity-item">
              <span className="activity-time">
                {new Date().toLocaleTimeString()}
              </span>
              <span className="activity-desc">
                {safeString(user.name)} logged in
              </span>
            </div>
            <div className="activity-item">
              <span className="activity-time">
                {new Date(Date.now() - 60000).toLocaleTimeString()}
              </span>
              <span className="activity-desc">
                System status checked
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderInvitations = () => (
    <div className="content-section">
      <div className="invitations-grid">
        <div className="admin-card has-form">
          <h3>Send New Invitation</h3>
          <form onSubmit={handleSendInvitation} className="invite-form">
            <div className="form-group">
              <label htmlFor="invite-full-name">Full Name</label>
              <input
                type="text"
                id="invite-full-name"
                value={inviteForm.full_name}
                onChange={(e) => setInviteForm({...inviteForm, full_name: e.target.value})}
                placeholder="John Doe"
                required
                autoComplete="off"
                data-1p-ignore="true"
                data-lpignore="true"
                data-bwignore="true"
                data-form-type="other"
              />
            </div>

            <div className="form-group">
              <label htmlFor="invite-email">Email Address</label>
              <input
                type="email"
                id="invite-email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                placeholder="user@gmail.com"
                required
                autoComplete="off"
                data-1p-ignore="true"
                data-lpignore="true"
                data-bwignore="true"
                data-form-type="other"
              />
            </div>

            <div className="form-group">
              <label htmlFor="invite-user-type">User Type</label>
              <CustomSelect
                  id="invite-user-type"
                  value={inviteForm.user_type}
                  onChange={(e) => setInviteForm({...inviteForm, user_type: e.target.value, agency_id: '', agency_name: ''})}
                >
                  <option value="admin">Admin</option>
                  <option value="operator">Operator</option>
                  <option value="client">Client</option>
                </CustomSelect>
            </div>

            {(inviteForm.user_type === 'admin' || inviteForm.user_type === 'operator') && (
              <div className="form-group">
                <label htmlFor="invite-agency">Agency *</label>
                <CustomSelect
                    id="invite-agency"
                    value={inviteForm.agency_id}
                    onChange={(e) => {
                      const selectedAgency = dashboardData?.data?.all_agencies?.find(c => (c._id || c.id) === e.target.value);
                      setInviteForm({
                        ...inviteForm,
                        agency_id: e.target.value,
                        agency_name: selectedAgency?.agency_name || ''
                      });
                    }}
                    required
                  >
                    <option value="">Select Agency</option>
                    {dashboardData?.data?.all_agencies?.map(agency => (
                      <option key={safeString(agency._id || agency.id)} value={safeString(agency._id || agency.id)}>
                        {safeString(agency.agency_name)}
                      </option>
                    ))}
                  </CustomSelect>
                <small className="form-hint">{inviteForm.user_type === 'admin' ? 'Admin' : 'Operator'} will be added to this agency</small>
              </div>
            )}

            {inviteForm.user_type === 'client' && (
              <>
                <div className="form-group">
                  <label htmlFor="invite-client-org">Organization (Optional)</label>
                  <CustomSelect
                      id="invite-client-org"
                      value={inviteForm.organization_id}
                      onChange={(e) => setInviteForm({...inviteForm, organization_id: e.target.value, brand_id: ''})}
                    >
                      <option value="">-- No specific organization --</option>
                      {dashboardData?.data?.organizations?.map(org => (
                        <option key={safeString(org._id || org.id)} value={safeString(org._id || org.id)}>
                          {safeString(org.organization_name || org.name)}
                        </option>
                      ))}
                    </CustomSelect>
                </div>
                {inviteForm.organization_id && (
                  <div className="form-group">
                    <label htmlFor="invite-client-brand">Brand (Optional)</label>
                    <CustomSelect
                        id="invite-client-brand"
                        value={inviteForm.brand_id}
                        onChange={(e) => setInviteForm({...inviteForm, brand_id: e.target.value})}
                      >
                        <option value="">-- No specific brand --</option>
                        {dashboardData?.data?.brands?.filter(brand =>
                          safeString(brand.organization_id) === inviteForm.organization_id
                        ).map(brand => (
                          <option key={safeString(brand._id || brand.id)} value={safeString(brand._id || brand.id)}>
                            {safeString(brand.brand_name || brand.name)}
                          </option>
                        ))}
                      </CustomSelect>
                  </div>
                )}
              </>
            )}


            <button
              type="submit"
              className="btn-lg btn-primary"
              disabled={inviteLoading}
            >
              {inviteLoading ? 'Sending...' : 'Send Invitation'}
            </button>
          </form>
        </div>

        <div className="admin-card">
          <h3>Pending Invitations ({pendingInvitations.length})</h3>
          <div className="invitations-list">
            {pendingInvitations.length === 0 ? (
              <p className="no-invitations">No pending invitations</p>
            ) : (
              pendingInvitations.map((invitation) => (
                <div key={safeString(invitation._id)} className="invitation-item">
                  <div className="invitation-info">
                    <div className="invitation-email">{safeString(invitation.email)}</div>
                    <div className="dashboard-invitation-details">
                      <span className="user-type">{safeString(invitation.user_type).replace('_', ' ')}</span>
                      <span className="created-date">
                        Sent: {safeDate(invitation.created_at)}
                      </span>
                      <span className="expires-date">
                        Expires: {safeDate(invitation.expires_at)}
                      </span>
                    </div>
                  </div>
                  <div className="invitation-status">
                    <span className="status-badge pending">Pending</span>
                    <button
                      className="cancel-invitation-btn"
                      onClick={() => handleCancelInvitation(safeString(invitation.token), safeString(invitation.email))}
                      disabled={cancellingInvitations.has(safeString(invitation.token))}
                      title="Cancel this invitation"
                    >
                      {cancellingInvitations.has(safeString(invitation.token)) ? (
                        <>Cancelling...</>
                      ) : (
                        <><Icon name="close-x" size="1rem" /> Cancel</>
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );


  if (loading) {
    return (
      <Layout
        user={user}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        onAgencyChange={handleAgencyChange}
        onOrganizationChange={handleOrganizationChange}
        onLogout={onLogout}
        activeAgency={activeAgency}
        orgRefreshTrigger={orgRefreshTrigger}
        showOrgSelector={false}
      >
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </Layout>
    );
  }

  // Determine if org/brand context is needed for current section
  // Super admins/admins don't need context for calendar - they can create meetings for any org/brand
  const needsOrgBrandContext = () => {
    const sectionsNeedingContext = [
      'recordings',
      'create-ideas',
      'operator-review-ideas',
      'review-content',
      'schedule-posts',
      'assets',
      'subscriptions',
      'create',
      'sources',
      'admin-organizations-brands'
    ];
    return sectionsNeedingContext.includes(activeSection);
  };

  const showContextSelectors = needsOrgBrandContext();

  return (
    <>
      <Layout
        user={user}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        contextMenuContent={showContextSelectors ? <h2>{getContextMenuTitle()}</h2> : null}
        onAgencyChange={handleAgencyChange}
        onOrganizationChange={handleOrganizationChange}
        onLogout={onLogout}
        onOpenWelcome={handleOpenWelcome}
        onOpenOnboarding={handleOpenOnboarding}
        activeAgency={activeAgency}
        orgRefreshTrigger={orgRefreshTrigger}
        showOrgSelector={showContextSelectors}
      >
        {renderContent()}
      </Layout>

      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        onComplete={handleWelcomeComplete}
      />

      <OnboardingRoleSelector
        isOpen={showOnboardingRoleSelector}
        onClose={() => setShowOnboardingRoleSelector(false)}
        onSelectRole={handleRoleSelect}
      />

      <OnboardingWizard
        isOpen={showOnboardingWizard}
        onClose={() => {
          setShowOnboardingWizard(false);
          setSelectedOnboardingRole(null);
        }}
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
        user={user}
        overrideRole={selectedOnboardingRole}
        onSectionChange={setActiveSection}
        currentSection={activeSection}
      />
    </>
  );
};

export default SuperAdminDashboard;